import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Avatar, Stack, Chip,
  TextField, MenuItem, Tooltip, Badge, IconButton, CircularProgress
} from "@mui/material";
import {
  CheckCircle as PresentIcon,
  Cancel as AbsentIcon,
  Save as SaveIcon,
  Medication as SickIcon,
  SportsEsports as ActivityIcon,
  NoteAdd as NoteIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon
} from "@mui/icons-material";
import { db, auth } from "../../firebase";
import {
  collection, getDocs, getDoc, query, where, doc, setDoc, Timestamp, orderBy
} from "firebase/firestore";
import Swal from "sweetalert2";

const PRIMARY = "#0d9488";

function HostelAttendance() {
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [timings, setTimings] = useState([]);
  const [hostels, setHostels] = useState([]);
  
  const [selectedTiming, setSelectedTiming] = useState("");
  const [selectedHostel, setSelectedHostel] = useState(""); // Replaced static hostelId with selectable state
  
  const [attendance, setAttendance] = useState({}); 
  const [notes, setNotes] = useState({});
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [schoolId, setSchoolId] = useState(null);
  const [userGender, setUserGender] = useState(null);
  const [isLocked, setIsLocked] = useState(false);

  // 1. Initial Setup: Get Warden's School, Hostel & Gender Context
  useEffect(() => {
    const init = async () => {
      try {
        if (!auth.currentUser) return;
        const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const sId = userData.schoolId;
          const hId = userData.hostelId; // Warden's default hostel
          const gender = userData.gender;
          
          setSchoolId(sId);
          setUserGender(gender);
          
          // Fetch Timings & Hostels simultaneously
          fetchTimings(sId);
          fetchHostels(sId, hId);
        }
      } catch (err) {
        console.error("Initialization Error:", err);
      }
    };
    init();
  }, []);

  const fetchTimings = async (sId) => {
    try {
      const q = query(collection(db, "hostel_attendance_timings"), orderBy("createdAt", "asc"));
      const snap = await getDocs(q);
      const timeList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTimings(timeList);
      if (timeList.length > 0) setSelectedTiming(timeList[0].id);
    } catch (err) {
      console.error("Error fetching timings:", err);
    }
  };

  const fetchHostels = async (sId, defaultHostelId) => {
    try {
      const q = query(collection(db, "hostels"), where("schoolId", "==", sId));
      const snap = await getDocs(q);
      const hList = snap.docs.map(d => ({ id: d.id, name: d.data().hostelName }));
      setHostels(hList);
      
      // Default to Warden's assigned hostel, otherwise the first available hostel
      if (defaultHostelId && hList.find(h => h.id === defaultHostelId)) {
        setSelectedHostel(defaultHostelId);
      } else if (hList.length > 0) {
        setSelectedHostel(hList[0].id);
      }
    } catch (err) {
      console.error("Error fetching hostels:", err);
    }
  };

  // 2. Fetch Attendance Data Scoped to Selected Hostel ID
  const fetchAttendanceData = useCallback(async () => {
    if (!schoolId || !selectedHostel || !selectedTiming) return;
    setLoading(true);
    try {
      // Document ID is unique per date, time slot, and hostel
      const attendanceId = `${date}_${selectedTiming}_${selectedHostel}`;
      
      const docRef = doc(db, "hostel_attendance", attendanceId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const savedRecords = data.records || {};
        
        const historicalList = Object.values(savedRecords)
          .map(r => ({
            id: r.studentId,
            name: r.name,
            roomNo: r.roomNo,
            gender: r.gender
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setStudents(historicalList);
        setAttendance(savedRecords);
        setNotes(data.notes || {});
        setIsLocked(true);
      } else {
        const qAlloc = query(
          collection(db, "roomallocations"),
          where("hostelId", "==", selectedHostel), 
          orderBy("studentName", "asc")
        );
        
        const allocSnap = await getDocs(qAlloc);
        const liveList = allocSnap.docs.map(d => ({
          id: d.data().studentId,
          name: d.data().studentName,
          roomNo: d.data().roomId,
          gender: d.data().studentGender
        }));

        setStudents(liveList);

        const initialAtt = {};
        liveList.forEach(s => {
          initialAtt[s.id] = {
            studentId: s.id,
            name: s.name,
            roomNo: s.roomNo,
            gender: s.gender || "",
            status: "Present" 
          };
        });

        setAttendance(initialAtt);
        setNotes({});
        setIsLocked(false);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
    }
    setLoading(false);
  }, [date, schoolId, selectedHostel, selectedTiming]);

  useEffect(() => {
    fetchAttendanceData();
  }, [fetchAttendanceData]);

  // 3. Status Change Handler
  const handleStatusChange = (id, newStatus) => {
    if (isLocked) return;
    setAttendance(prev => ({
      ...prev,
      [id]: { ...prev[id], status: newStatus }
    }));
  };

  const handleNoteChange = async (id, currentNote) => {
    if (isLocked) return;
    const { value: text } = await Swal.fire({
      title: 'Warden Note',
      input: 'textarea',
      inputLabel: `Note for ${attendance[id]?.name}`,
      inputValue: currentNote || '',
      showCancelButton: true
    });
    if (text !== undefined) setNotes(prev => ({ ...prev, [id]: text }));
  };

  // 4. Persistence Scoped to Selected Hostel ID
  const saveAttendance = async () => {
    if (!selectedHostel) return;
    setLoading(true);
    try {
      const attendanceId = `${date}_${selectedTiming}_${selectedHostel}`;
      const docRef = doc(db, "hostel_attendance", attendanceId);

      await setDoc(docRef, {
        date,
        schoolId,
        hostelId: selectedHostel,
        timingId: selectedTiming,
        records: attendance,
        notes: notes,
        updatedAt: Timestamp.now(),
        markedBy: auth.currentUser?.uid || ""
      });

      setIsLocked(true);
      Swal.fire("Finalized", "Attendance record is now locked.", "success");
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
    setLoading(false);
  };

  // 5. Analytics Stats (Derived directly from current student list)
  const stats = {
    total: students.length,
    present: Object.values(attendance).filter(v => v.status === "Present").length,
    absent: Object.values(attendance).filter(v => v.status === "Absent").length,
    sick: Object.values(attendance).filter(v => v.status === "Sick").length,
    activity: Object.values(attendance).filter(v => v.status === "Activity").length,
  };

  // Dynamically display the selected hostel's name
  const currentHostelName = hostels.find(h => h.id === selectedHostel)?.name || "Hostel Attendance";

  return (
    <Box p={2} sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* TITLE BANNER */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#fff" }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: PRIMARY }}>
            <HomeIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold" color="textPrimary">
              {currentHostelName}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Logged in Warden Gender Focus: <strong>{userGender || "N/A"}</strong>
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* STATS ROW */}
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
        {[
          { label: "Total", val: stats.total, color: "#64748b" },
          { label: "Present", val: stats.present, color: "#10b981" },
          { label: "Sick", val: stats.sick, color: "#f59e0b" },
          { label: "Activity", val: stats.activity, color: "#6366f1" },
          { label: "Absent", val: stats.absent, color: "#ef4444" },
        ].map((item) => (
          <Paper key={item.label} sx={{ p: 1.5, flex: 1, minWidth: 120, borderLeft: `4px solid ${item.color}` }}>
            <Typography variant="caption" fontWeight="bold" color="textSecondary">{item.label}</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: item.color }}>{item.val}</Typography>
          </Paper>
        ))}
      </Stack>

      {/* FILTERS */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }} elevation={0}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          
          {/* Hostel Selection */}
          <TextField
            select label="Hostel" size="small" fullWidth
            value={selectedHostel}
            onChange={(e) => setSelectedHostel(e.target.value)}
          >
            {hostels.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
          </TextField>

          <TextField
            select label="Slot" size="small" fullWidth
            value={selectedTiming}
            onChange={(e) => setSelectedTiming(e.target.value)}
          >
            {timings.map(t => <MenuItem key={t.id} value={t.id}>{t.title} ({t.displayTime})</MenuItem>)}
          </TextField>

          <TextField
            type="date" label="Date" size="small" fullWidth
            value={date} onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          
          <Chip 
            label={`Total: ${students.length}`} 
            color="primary" 
            variant="outlined" 
            sx={{ fontWeight: 'bold', fontSize: '0.9rem', minWidth: '100px' }} 
          />

          {isLocked && (
            <Chip icon={<HistoryIcon />} label="Saved Record (Locked)" color="info" variant="outlined" sx={{ fontWeight: 'bold' }} />
          )}
        </Stack>
      </Paper>

      {/* TABLE */}
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}>
        {loading ? (
          <Box p={5} textAlign="center"><CircularProgress size={30} sx={{ color: PRIMARY }} /></Box>
        ) : (
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f1f5f9" }}>
              <TableRow>
                <TableCell width="50px">Sr.</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Room</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center"><Typography p={2} color="textSecondary">No students found in this hostel</Typography></TableCell></TableRow>
              ) : (
                students.map((s, idx) => (
                  <TableRow key={s.id} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 32, height: 32, bgcolor: PRIMARY, fontSize: 14 }}>{s.name[0]}</Avatar>
                        <Typography variant="body2" fontWeight="500">{s.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell><Chip label={s.roomNo} size="small" variant="outlined" /></TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        {[
                          { icon: <PresentIcon />, status: "Present", color: "success" },
                          { icon: <SickIcon />, status: "Sick", color: "warning" },
                          { icon: <ActivityIcon />, status: "Activity", color: "secondary" },
                          { icon: <AbsentIcon />, status: "Absent", color: "error" },
                        ].map((btn) => {
                          const isActive = attendance[s.id]?.status === btn.status;
                          return (
                            <Tooltip key={btn.status} title={btn.status}>
                              <IconButton
                                size="small"
                                disabled={isLocked}
                                onClick={() => handleStatusChange(s.id, btn.status)}
                                sx={{
                                  color: isActive ? 'white' : `${btn.color}.main`,
                                  bgcolor: isActive ? `${btn.color}.main` : 'transparent',
                                  border: `1px solid ${isActive ? 'transparent' : '#cbd5e1'}`,
                                  transition: 'all 0.2s',
                                  '&:hover': { 
                                    bgcolor: isActive ? `${btn.color}.dark` : `${btn.color}.light` 
                                  },
                                  '&.Mui-disabled': {
                                    color: isActive ? 'white' : `${btn.color}.main`,
                                    bgcolor: isActive ? `${btn.color}.main` : 'transparent',
                                    opacity: isActive ? 1 : 0.45,
                                    borderColor: isActive ? 'transparent' : '#cbd5e1'
                                  }
                                }}
                              >
                                {btn.icon}
                              </IconButton>
                            </Tooltip>
                          );
                        })}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        size="small" 
                        onClick={() => handleNoteChange(s.id, notes[s.id])}
                        disabled={isLocked && !notes[s.id]}
                      >
                        <Badge color="error" variant="dot" invisible={!notes[s.id]}>
                          <NoteIcon fontSize="small" color={notes[s.id] ? "primary" : "disabled"} />
                        </Badge>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

        <Box p={2} textAlign="right" sx={{ borderTop: "1px solid #e2e8f0", bgcolor: "#fff" }}>
          {!isLocked ? (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: "#0f766e" } }}
              onClick={saveAttendance}
              disabled={loading || students.length === 0}
            >
              Confirm & Lock Attendance
            </Button>
          ) : (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              color="inherit"
              onClick={fetchAttendanceData}
            >
              Refresh View
            </Button>
          )}
        </Box>
      </TableContainer>
    </Box>
  );
}

export default HostelAttendance;